import React, { Component } from "react";
import { observable, toJS } from "mobx";
import { observer, inject } from "mobx-react";
import { Button, Dropdown, Menu, message, Modal, Tooltip, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faTable,
  faFile,
  faLink,
  faDatabase,
  faPlug,
  faTerminal,
  faFlask,
} from "@fortawesome/free-solid-svg-icons";

import { config } from "../../../config/configUi";
import dayjs from "dayjs";

/**
 * @tutorial
 * This component contains several functions
 * designed to facilitate and speed up the development and testing of CRUDs.
 * These features will only be available in development and testing environments:
 * they will never be rendered in production.
 */

interface CrudDeveloperOptionsProps {
  controller?: any;

  item: string; // object that represent a single element of the dataset, typically used for the form data
  itemName?: string; // For example "Device", User" or "Job Item Logic Matrix"
  collectionName: string; // name of the variable containing the main data of the CRUD
  fillCreateForm?: any; // specify what values you want to fill the form with when the "Fill Create Form" button is clicked
  v1PageLink?: string; // URL of the old version of the CRUD, if any
  model?: any; // an HTML representation of the CRUD model
  apiCallsList?: Array<{ requestURL: string; requestMethod: string; callType: string }>; // an HTML representation of the calls that can be made within the CRUD
  representativeField?: string; // name of the main identifier field of the CRUD, usually the ref. You have to pass this parameter to activate the "View Documents IDs" option
}

// @inject("controller")
@observer
export class CrudDeveloperOptions extends Component<CrudDeveloperOptionsProps> {
  @observable modalContent: any;
  @observable modalTitle: string = "";
  @observable modalVisibility: boolean = false;

  private renderModalContent = (e: any) => {
    const { collectionName, representativeField, itemName, model } = this.props;

    switch (e.key) {
      case "viewModel":
        this.modalTitle = (itemName || "CRUD") + " Model";

        this.modalContent = model;
        break;
      case "viewAPICalls":
        this.modalTitle = "API Calls";

        this.modalContent = this.props.apiCallsList.map((element, index) => {
          console.log("called viewAPICalls");

          return (
            <div key={index}>
              <h5 className="text-orange-500">{index + 1}</h5>
              <ul>
                <li>
                  <strong>• Request URL</strong>: {element.requestURL}
                </li>
                <li>
                  <strong>• Request Method</strong>: {element.requestMethod}
                </li>
                <li>
                  <strong>• Call Type</strong>: {element.callType}
                </li>
              </ul>
              <br />
            </div>
          );
        });
        break;
      case "viewDocumentsIds":
        this.modalTitle = "Documents IDs";

        this.modalContent = this.props.controller[collectionName].map((element, index) => {
          console.log("called viewDocumentsIds");

          return (
            <div key={index}>
              <h5 className="text-orange-500">{index + 1}</h5>
              <ul>
                <li>
                  <strong>• {representativeField}</strong>: {element[representativeField]}
                </li>
                <li>
                  <strong>• _id</strong>:{" "}
                  <Typography.Paragraph copyable style={{ display: "inline" }}>
                    {element._id}
                  </Typography.Paragraph>
                </li>
              </ul>
              <br />
            </div>
          );
        });
        break;
    }

    this.modalVisibility = true;
  };

  private viewModal = () => {
    return (
      <Modal
        title={this.modalTitle}
        open={this.modalVisibility}
        footer={null}
        onCancel={() => (this.modalVisibility = false)}
        closable={true}
        centered={true}
        width="auto"
        // bodyStyle={{ padding: "8px 16px 0px 16px", maxHeight: "50vh", overflowY: "auto", fontSize: "1.15em" }}

        styles={{
          body: { padding: "8px 16px 0px 16px", maxHeight: "50vh", overflowY: "auto", fontSize: "1.15em" },
        }}
      >
        {this.modalContent}
      </Modal>
    );
  };

  private renderDeveloperOptions = () => {
    const { item, collectionName, fillCreateForm, v1PageLink, model, apiCallsList, representativeField } = this.props;

    const developerOptionsMenu = (
      <Menu>
        <li style={{ padding: "5px 12px" }}>
          <strong>Developer Options</strong>
        </li>

        <Menu.Divider style={{ margin: 0 }} />

        <Menu.Item
          key="simulateReadOnly"
          disabled={this.props.controller.isReadOnly}
          onClick={() => {
            this.props.controller.isReadOnly = true;

            // Necessary to recompute the columns and thus correctly rerender the table
            this.props.controller.setAdditionalColumnsAreVisible(!this.props.controller.additionalColumnsAreVisible);
            setTimeout(() => {
              this.props.controller.setAdditionalColumnsAreVisible(!this.props.controller.additionalColumnsAreVisible);
            }, 10);

            message.info("Switched to Read-Only mode");
          }}
        >
          <FontAwesomeIcon icon={faEye} style={{ marginRight: 5 }} />
          <span>Simulate Read-Only</span>
        </Menu.Item>

        <Menu.Item
          key="testRows"
          disabled={this.props.controller[collectionName]?.length === 0}
          onClick={() => {
            this.props.controller[collectionName].forEach((element, index) => {
              console.log("called simulate rows");

              setTimeout(() => {
                this.props.controller.openEditForm(element);
                this.props.controller.tableInstance.selectedRow.row = element._id;

                if (document.querySelector(".OFTable")) {
                  console.log(`%c Row #${index + 1} rendered`, "color: #52c41a");
                } else {
                  console.log(`%c Row #${index + 1} crashed`, "color: #f5222d");
                }
              }, index * 100);
            });
          }}
        >
          <Tooltip
            placement="left"
            title={
              "Estimated execution time: " + dayjs(this.props.controller[collectionName]?.length * 100).format("mm:ss")
            }
          >
            <FontAwesomeIcon icon={faTable} style={{ marginRight: 5 }} />

            <span>Test Rows</span>
          </Tooltip>
        </Menu.Item>

        {fillCreateForm && (
          <Menu.Item
            key="fillCreateForm"
            disabled={this.props.controller.isReadOnly}
            onClick={() => {
              this.props.controller.formMode = "create";
              this.props.controller.tableInstance.selectedRow.row = null;
              this.props.controller[item] = toJS(fillCreateForm);
              this.props.controller.setIsFormModalVisible(true);
            }}
          >
            <FontAwesomeIcon icon={faFile} style={{ marginRight: 5 }} />
            <span>Fill Create Form</span>
          </Menu.Item>
        )}

        {v1PageLink && (
          <Menu.Item key="openV1Page" onClick={() => window.open(v1PageLink, "_blank", "noreferrer")}>
            <FontAwesomeIcon icon={faLink} style={{ marginRight: 5 }} />
            <span>Open v1 Page</span>
          </Menu.Item>
        )}

        <Menu.Divider style={{ margin: 0 }} />

        {model && (
          <Menu.Item key="viewModel" onClick={this.renderModalContent}>
            <FontAwesomeIcon icon={faDatabase} style={{ marginRight: 5 }} />
            <span>View Model</span>
          </Menu.Item>
        )}

        {apiCallsList && (
          <Menu.Item key="viewAPICalls" onClick={this.renderModalContent}>
            <FontAwesomeIcon icon={faPlug} style={{ marginRight: 5 }} />
            <span>View API Calls</span>
          </Menu.Item>
        )}

        {representativeField && (
          <Menu.Item
            key="viewDocumentsIds"
            disabled={this.props.controller[collectionName]?.length === 0}
            onClick={this.renderModalContent}
          >
            <FontAwesomeIcon icon={faTerminal} style={{ marginRight: 5 }} />
            <span>View Documents IDs</span>
          </Menu.Item>
        )}
      </Menu>
    );

    return (
      <Dropdown overlay={developerOptionsMenu} trigger={["click"]}>
        <Button
          type="default"
          icon={<FontAwesomeIcon icon={faFlask} />}
          size="small"
          style={{ minWidth: 24, color: "#fa8c16", backgroundColor: "#fff7e6", borderColor: "#ffd591" }}
        />
      </Dropdown>
    );
  };

  public render() {
    if (!config.configOverride) {
      return (
        <>
          {this.renderDeveloperOptions()}
          {this.viewModal()}
        </>
      );
    } else {
      return null;
    }
  }
}
